import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Plus, RefreshCw, Settings2, Sparkles, Trash2 } from 'lucide-react'

import { deleteCreativeWorkspace, listCreativeWorkspaces, type CreativeWorkspace } from '@/api/studio-creative'
import { AuthSessionExpiredError } from '@/api/authSession'
import { CATALOG_LIST_GRID_CLASS } from '@/shared/components/catalog/catalogListGrid'
import { CatalogListSkeleton } from '@/shared/components/catalog/CatalogListSkeleton'
import { CatalogListToolbar, CatalogListPrimaryButton } from '@/shared/components/catalog/CatalogListToolbar'
import { PageEmptyState, PageEmptyStateActionButton } from '@/shared/components/PageEmptyState'
import { CreateCreativeWorkspaceModal } from '@/features/studio/modals/CreateCreativeWorkspaceModal'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { CreativeLibraryTabs } from '@/features/studio/components/CreativeLibraryTabs'
import { StudioCatalogCard } from '@/features/studio/components/StudioCatalogCard'
import { StudioHubPageShell } from '@/features/studio/components/StudioHubPageShell'
import { ProtoIconBtn, ProtoPagination } from '@/features/studio/components/ui-kit'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import { useClientPagination } from '@/features/studio/hooks/useClientPagination'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { STUDIO_BASE } from '@/features/studio/lib/studio-paths'
import { Button } from '@/shared/components/ui/button'

const PAGE_SIZE = 12
const FETCH_LIMIT = 200

function formatRelativeTime(iso: string): string {
  const ts = Date.parse(iso)
  if (!Number.isFinite(ts)) return iso
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return '刚刚'
  if (mins < 60) return `${mins} 分钟前`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} 小时前`
  return new Date(ts).toLocaleString()
}

export function CreativeHubPage() {
  const { t } = useTranslation()
  const [items, setItems] = useState<CreativeWorkspace[]>([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<CreativeWorkspace | null>(null)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setErrorKey(null)
    try {
      const res = await listCreativeWorkspaces(FETCH_LIMIT, 0)
      setItems(res.items)
    } catch (e) {
      if (e instanceof AuthSessionExpiredError) return
      setErrorKey(resolveApiErrorMessage(e, 'creative.hub.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const { page, setPage, total, items: pageItems } = useClientPagination(items, PAGE_SIZE)

  async function confirmDelete() {
    if (!deleteTarget) return
    setDeleteBusy(true)
    setErrorKey(null)
    try {
      await deleteCreativeWorkspace(deleteTarget)
      setDeleteTarget(null)
      await reload()
    } catch (e) {
      if (e instanceof AuthSessionExpiredError) return
      setErrorKey(resolveApiErrorMessage(e, 'creative.hub.deleteFailed'))
    } finally {
      setDeleteBusy(false)
    }
  }

  const errorMessage =
    errorKey === 'sessionExpired'
      ? t('studio.sessionExpired')
      : errorKey
        ? t(errorKey, { defaultValue: errorKey })
        : null

  return (
    <>
      <StudioHubPageShell mode="creative">
        <CreativeLibraryTabs active="workspaces" />
        <CatalogListToolbar
          actions={
            <CatalogListPrimaryButton onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('creative.hub.create')}
            </CatalogListPrimaryButton>
          }
        />

        {errorMessage ? (
          <div
            className="mb-4 flex gap-3 rounded-lg border border-rose-500/25 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
            role="alert"
          >
            <AlertCircle className="h-5 w-5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p>{errorMessage}</p>
              {errorKey === 'sessionExpired' ? (
                <Link
                  to={`/auth/login?redirect=${encodeURIComponent(`${STUDIO_BASE}/creative`)}`}
                  className="mt-2 inline-block text-xs text-rose-300 underline underline-offset-2"
                >
                  {t('studio.reenterFromPortal')}
                </Link>
              ) : (
                <Button
                  type="button"
                  variant="link"
                  onClick={() => void reload()}
                  className="mt-2 h-auto gap-1 p-0 text-xs text-rose-300 underline-offset-2"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {t('common.retry')}
                </Button>
              )}
            </div>
          </div>
        ) : null}

        {loading ? (
          <CatalogListSkeleton />
        ) : items.length === 0 ? (
          <PageEmptyState icon={Sparkles} tone="violet" title={t('creative.hub.emptyTitle')} subtitle={t('creative.hub.empty')}>
            <PageEmptyStateActionButton tone="amber" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('creative.hub.createFirst')}
            </PageEmptyStateActionButton>
          </PageEmptyState>
        ) : (
          <>
            <ul className={CATALOG_LIST_GRID_CLASS}>
              {pageItems.map((row, idx) => {
                const title =
                  row.title?.trim() || t('creative.hub.defaultTitle', { n: idx + 1 + page * PAGE_SIZE })
                const cover = resolveMediaDisplayUrl(row.cover_url)
                return (
                  <StudioCatalogCard
                    key={row.public_id}
                    href={`${STUDIO_BASE}/creative/${row.public_id}/canvas`}
                    title={title}
                    description={row.summary?.trim() || undefined}
                    coverUrl={cover || null}
                    fallback={
                      <div className="flex h-full flex-col items-center justify-center gap-2 text-violet-500/35">
                        <Sparkles size={36} strokeWidth={1.25} aria-hidden />
                        <span className="max-w-[80%] truncate text-2xl font-semibold text-zinc-700">
                          {title.slice(0, 1) || '?'}
                        </span>
                      </div>
                    }
                    overlayActions={
                      <>
                        <ProtoIconBtn
                          aria-label={t('common.edit')}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setEditTarget(row)
                          }}
                        >
                          <Settings2 className="h-4 w-4" />
                        </ProtoIconBtn>
                        <ProtoIconBtn
                          tone="danger"
                          aria-label={t('common.delete')}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setDeleteTarget(row.public_id)
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </ProtoIconBtn>
                      </>
                    }
                    meta={<span>{formatRelativeTime(row.updated_at)}</span>}
                  />
                )
              })}
            </ul>
            <ProtoPagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
          </>
        )}
      </StudioHubPageShell>

      <CreateCreativeWorkspaceModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void reload()}
      />

      <CreateCreativeWorkspaceModal
        open={editTarget != null}
        editing={editTarget}
        onClose={() => setEditTarget(null)}
        onCreated={() => void reload()}
      />

      <ConfirmDialog
        open={deleteTarget != null}
        title={t('creative.hub.delete')}
        message={t('creative.hub.deleteConfirm')}
        confirmLabel={t('common.delete')}
        destructive
        busy={deleteBusy}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  )
}
